import clsx from 'clsx';
import { hasPermission } from 'Components/Authorize';
import { Banknote, Book, CalendarDays, ChartLine, Gift, Heart, Image, Info, PawPrint, Star, User } from 'lucide-preact';
import { ComponentProps, ReactNode } from 'preact/compat';
import { useDispatch } from 'react-redux';

import { getHomeURL } from '@/helpers';
import { cn } from '@/lib/utils';
import { useSelector } from '@/redux/helper';
import { selectCanAccessEducatorsApp, selectIsProfileCompleted } from '@/redux/selectors/users';
import { setEducatorsAppVisited } from '@/redux/slices/settings/visitedLinks/action';
import { partnerMPLinkFetcher } from '@/shared/fetch';

interface MenuRowProps extends Omit<ComponentProps<'a'>, 'icon'> {
  title: string;
  icon?: ReactNode;
}

interface NavMenuContentProps extends ComponentProps<'div'> {
  onItemClick?: (e: MouseEvent) => void;
}

const MenuRow = ({ title, children, icon, ...rest }: MenuRowProps) => (
  <a
    className="preflight preflight-a flex items-center gap-2 px-6 py-3 text-base font-bold text-stone-900 hover:bg-[#ebebeb]"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...rest}
  >
    {icon}
    {title && <span>{title}</span>}
    {children}
  </a>
);

const NavMenuContent = ({ className, onItemClick, ...props }: NavMenuContentProps) => {
  const dispatch = useDispatch();
  const educatorsAppVisited = useSelector((state) => state.settings.visitedLinks.educatorsApp);
  const canAccessEducatorsApp = useSelector(selectCanAccessEducatorsApp);
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  const giftCardConfig = useSelector((state) => state.remoteConfig.giftCardConfig);
  const username = useSelector((store) => store.user.username);
  const isProfileCompleted = useSelector(selectIsProfileCompleted);
  const isLoggedIn = !!username;
  const rootDomainURL = getHomeURL();

  const educatorsAppLink = educatorsAppURL ? new URL(educatorsAppURL) : undefined;
  if (educatorsAppLink) {
    educatorsAppLink.pathname = '/about';
  }

  const divider = <div className="mx-6 my-3 h-px bg-stone-300" />;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div className={cn('py-3', className)} {...props}>
      {!isLoggedIn ? (
        <>
          <MenuRow
            title="About"
            data-item="about"
            icon={<PawPrint className="size-5" />}
            href={`${rootDomainURL}/about-us`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="Features"
            data-item="features"
            icon={<Star className="size-5" />}
            href={`${rootDomainURL}/features`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="For Educators"
            data-item="educators"
            icon={<Book className="size-5" />}
            href={`${rootDomainURL}/educators`}
            onClick={onItemClick}
          />
          {divider}
          <MenuRow
            title="Events"
            data-item="events"
            icon={<CalendarDays className="size-5" />}
            href="/talk-schedule"
            onClick={onItemClick}
          />
        </>
      ) : (
        <>
          {hasPermission('zoo:view-partner-statistics-link') && (
            <MenuRow
              title="Partner Analytics"
              data-item="partner-analytics"
              onClick={(e) => {
                partnerMPLinkFetcher().then((link) => {
                  if (link) {
                    window.open(link);
                  }
                });
                onItemClick?.(e);
              }}
              icon={<ChartLine className="size-5" />}
            />
          )}

          {canAccessEducatorsApp && (
            <div className="group px-6 py-3 hover:bg-[#ebebeb]">
              <a
                href={educatorsAppLink?.toString()}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  'preflight preflight-a flex size-full items-center gap-2 font-bold',
                  educatorsAppVisited
                    ? 'text-stone-900'
                    : 'animate-pulsingGradientText bg-[linear-gradient(to_right,#374151,#374151,#065f46,#047857,#059669,#10b981,#34d399,#6ee7b7,#34d399,#10b981,#059669,#047857,#065f46,#374151,#374151)] bg-[size:300%_100%] bg-clip-text text-transparent',
                )}
                onClick={(e) => {
                  dispatch(setEducatorsAppVisited(true));
                  onItemClick?.(e);
                }}
              >
                <Book className="size-5 text-stone-900" />
                Educator Resources
              </a>
            </div>
          )}

          <MenuRow
            title="My Animals"
            data-item="my-animals"
            href="/habitats"
            onClick={onItemClick}
            icon={<Heart className="size-5" />}
          />
          <MenuRow title="My Album" href={`/u/${username}`} onClick={onItemClick} icon={<Image className="size-5" />} />

          {divider}

          <MenuRow
            title="My Account"
            data-item="my-account"
            href="/account/info"
            onClick={onItemClick}
            icon={<User className="size-5" />}
          >
            {!isProfileCompleted && (
              <span className="block rounded-full bg-rose-50 px-2 text-[10px]/[1rem] font-medium uppercase text-rose-500">
                Complete your profile
              </span>
            )}
          </MenuRow>

          {hasPermission('subscription:edit') && (
            <MenuRow
              title="Plans"
              data-item="plans"
              href="/plans"
              onClick={onItemClick}
              icon={<Banknote className="size-5" />}
            />
          )}
          {divider}

          <MenuRow
            title="Events"
            data-item="events"
            icon={<CalendarDays className="size-5" />}
            href="/schedule"
            onClick={onItemClick}
          />

          {divider}

          <MenuRow
            title="Gift Cards"
            data-item="gift-cards"
            href="/gift"
            onClick={onItemClick}
            icon={
              giftCardConfig?.enabled && giftCardConfig?.iconURL ? (
                <img className="-mx-0.5 -mt-0.5 w-6" src={giftCardConfig.iconURL} alt="gift box" />
              ) : (
                <Gift className="size-5" />
              )
            }
          >
            {giftCardConfig?.enabled && giftCardConfig?.badge && (
              <span className="block rounded-full bg-olive-4 px-2 text-[10px]/[1rem] font-medium">
                {giftCardConfig.badge}
              </span>
            )}
          </MenuRow>

          <MenuRow title="Support" data-item="help" href="/help" onClick={onItemClick} icon={<Info className="size-5" />} />

          {hasPermission('admin:menu') && (
            <>
              {divider}
              <MenuRow title="Admin - Users" data-item="admin-users" href="/admin/users" onClick={onItemClick} />
              <MenuRow title="Admin - Habitats" data-item="admin-habitats" href="/admin/habitats" onClick={onItemClick} />
              <MenuRow title="Admin - Partners" data-item="admin-partners" href="/admin/partners" onClick={onItemClick} />
              <MenuRow
                title="Admin - Camera Settings"
                data-item="admin-camera-settings"
                href="/admin/camera/settings"
                onClick={onItemClick}
              />
              <MenuRow
                title="Admin - Camera Info"
                data-item="admin-camera-info"
                href="/admin/camera/info"
                onClick={onItemClick}
              />
              <MenuRow title="Admin - Playlists" href="/admin/playlists" onClick={onItemClick} />
              <MenuRow title="Admin - Announcements" href="/admin/announcements" onClick={onItemClick} />
              <MenuRow title="Admin - Advertisements" href="/admin/advertisements" onClick={onItemClick} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NavMenuContent;
